// ✅ src/pages/VehicleV2/MonthlyWorkingHrReport_Print.jsx
// React PRINT page for: MonthlyWorkingHrReportPrint.aspx (Crystal)
// - Uses your sample print template style (OwnEquipmentReport)
// - Fetches print rows from backend and renders an HTML printable report
//
// ✅ Expected backend:
//   GET /api/monthlyWorkingHrs/print
//     - returns { success:true, rows:[...], count:n, meta:{fromDate,toDate,branch,store,month,year} }
//
// ✅ URL example:
// /vehicle/MonthlyWorkingHrReport_Print?branchId=8&branchType=HO&loginUserId=37&
//   fromDate=2026-02-01&toDate=2026-02-20&month=February&year=2026&branch=Select&store=Select
//
// NOTE:
// - ASPX Crystal report used Session["VehPrint"] DataTable.
// - React version pulls directly from API instead.
// - Uses window.print() like your sample.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const API_BASE = "https://nissi-india.com/api/monthlyWorkinghrsRpt";

const toStr = (v) => String(v ?? "").trim();
const toNumStr = (v, d = "0.00") => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return def;
};

const readQuery = (search) => {
  const qs = new URLSearchParams(search || window.location.search);
  const get = (k, d = "") => toStr(qs.get(k) ?? d);

  return {
    branchId: get("branchId") || get("branchid") || "",
    branchType: (get("branchType") || get("branchtype") || "HO").toUpperCase(),
    loginUserId: get("loginUserId") || get("loginuserid") || "",
    fromDate: get("fromDate") || get("fromdate") || "",
    toDate: get("toDate") || get("todate") || "",
    month: get("month") || "Select",
    year: get("year") || "Select",
    branch: get("branch") || "Select", // ddl_branch selection
    store: get("store") || "Select",
  };
};

// column definitions (match your ASPX header order)
const monthDefs = [
  { label: "April", hr: "fld_hr1", km: "fld_km1" },
  { label: "May", hr: "fld_hr2", km: "fld_km2" },
  { label: "June", hr: "fld_hr3", km: "fld_km3" },
  { label: "July", hr: "fld_hr4", km: "fld_km4" },
  { label: "August", hr: "fld_hr5", km: "fld_km5" },
  { label: "September", hr: "fld_hr6", km: "fld_km6" },
  { label: "October", hr: "fld_hr7", km: "fld_km7" },
  { label: "November", hr: "fld_hr8", km: "fld_km8" },
  { label: "December", hr: "fld_hr9", km: "fld_km9" },
  { label: "January", hr: "fld_hr10", km: "fld_km10" },
  { label: "February", hr: "fld_hr11", km: "fld_km11" },
  { label: "March", hr: "fld_hr12", km: "fld_km12" },
];

export default function MonthlyWorkingHrReport_Print() {
  const navigate = useNavigate();
  const { search, state } = useLocation();

  // supports BOTH navigation state and querystring
  const params = useMemo(() => {
    const qs = readQuery(search);
    const st = state?.filters || state || {};
    return {
      branchId: toStr(st.branchId || st.branchid || qs.branchId),
      branchType: toStr(st.branchType || st.branchtype || qs.branchType || "HO").toUpperCase(),
      loginUserId: toStr(st.loginUserId || st.loginuserid || qs.loginUserId),
      fromDate: toStr(st.fromDate || st.fromdate || qs.fromDate),
      toDate: toStr(st.toDate || st.todate || qs.toDate),
      month: toStr(st.month || qs.month || "Select"),
      year: toStr(st.year || qs.year || "Select"),
      branch: toStr(st.branch || qs.branch || "Select"),
      store: toStr(st.store || qs.store || "Select"),
    };
  }, [search, state]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [meta, setMeta] = useState({
    fromDate: "",
    toDate: "",
    month: "",
    year: "",
    branchName: "",
    storeName: "",
    screen: "INFRA SCREEN : B-40/2",
  });

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const fetchPrint = async () => {
      try {
        if (!params.branchId || !params.loginUserId) {
          setErrMsg("Missing required params (branchId/loginUserId). Please open from report page.");
          return;
        }

        setLoading(true);
        setErrMsg("");

        const { data } = await axios.get(`${API_BASE}/monthly`, {
          params: {
            branchId: params.branchId,
            branchType: params.branchType,
            loginUserId: params.loginUserId,

            fromDate: params.fromDate,
            toDate: params.toDate,
            month: params.month,
            year: params.year,
            branch: params.branch,
            store: params.store,
          },
        });

        if (!data?.success) throw new Error(data?.message || "Failed to load print data");

        setRows(Array.isArray(data.rows) ? data.rows : []);
        const m = data.meta || {};
        setMeta((p) => ({
          ...p,
          fromDate: m.fromDate ?? params.fromDate ?? "",
          toDate: m.toDate ?? params.toDate ?? "",
          month: m.month ?? (params.month === "Select" ? "" : params.month),
          year: m.year ?? (params.year === "Select" ? "" : params.year),
          branchName: m.branchName ?? (params.branch === "Select" ? "" : params.branch),
          storeName: m.storeName ?? (params.store === "Select" ? "" : params.store),
        }));
      } catch (err) {
        console.error(err);
        const msg = err?.response?.data?.message || err?.message || "Failed to load print report";
        setErrMsg(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchPrint();
  }, [params]);

  // totals (optional footer totals across all rows & months)
  const totals = useMemo(() => {
    const t = {};
    for (const m of monthDefs) {
      t[m.hr] = 0;
      t[m.km] = 0;
    }

    for (const r of rows) {
      for (const m of monthDefs) {
        const hr = Number(pick(r, [m.hr], "0").replace(/,/g, ""));
        const km = Number(pick(r, [m.km], "0").replace(/,/g, ""));
        if (Number.isFinite(hr)) t[m.hr] += hr;
        if (Number.isFinite(km)) t[m.km] += km;
      }
    }

    // round
    for (const k of Object.keys(t)) t[k] = Math.round((t[k] + Number.EPSILON) * 100) / 100;
    return t;
  }, [rows]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 8px; font-size: 10pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #000 !important; padding: 3px !important; }
          .print-landscape { 
            size: A4 landscape; 
            margin: 0.25in;
          }
        }
        @page {
          size: A4 landscape;
          margin: 0.25in;
        }
      `}</style>

      {/* Top Bar (no-print) */}
      <div className="no-print flex items-center justify-between p-3 border-b">
        <div className="text-sm">
          <span className="font-semibold">Monthly Working Hours Report</span>
          {loading && <span className="ml-3">Loading...</span>}
          {errMsg && <span className="ml-3 text-red-600">{errMsg}</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded">
            Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            disabled={loading}
          >
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Page */}
      <div className="p-3 print:p-2 print-landscape">
        <div className="flex justify-end text-xs font-semibold mb-2">{meta.screen}</div>

        <div className="border-2 border-black">
          {/* Header */}
          <div className="border-b-2 border-black">
            <div className="flex items-center p-3">
              <div className="w-24 h-20 border-2 border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
                <div className="text-center">
                  <div className="text-xs">NISSI</div>
                  <div className="text-xs">INFOTECH</div>
                  <div className="text-xs">PVT LTD</div>
                </div>
              </div>

              <div className="flex-1 text-center">
                <h1 className="text-xl font-bold">Nissi Infotech</h1>
                <div className="text-sm font-semibold">Nissi</div>
                <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
                <div className="text-xs mt-1">
                  3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                </div>
                <div className="text-xs">
                  Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  infracnsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center py-2 border-b-2 border-black bg-gray-100">
            <h2 className="text-lg font-bold">Vehicle Monthly Working Hours Report</h2>
          </div>

          {/* Filters summary */}
          <div className="border-b-2 border-black p-2 text-xs">
            <div className="grid grid-cols-4 gap-2">
              <div>
                <span className="font-semibold">From</span> : {meta.fromDate || "-"}
              </div>
              <div>
                <span className="font-semibold">To</span> : {meta.toDate || "-"}
              </div>
              <div>
                <span className="font-semibold">Month</span> : {meta.month || "-"}
              </div>
              <div>
                <span className="font-semibold">Year</span> : {meta.year || "-"}
              </div>
              <div>
                <span className="font-semibold">Branch</span> : {meta.branchName || "-"}
              </div>
              <div>
                <span className="font-semibold">Store</span> : {meta.storeName || "-"}
              </div>
              <div className="col-span-2">
                <span className="font-semibold">Total Vehicles</span> : {rows.length}
              </div>
            </div>
          </div>

          {/* Table */}
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm">No Records Found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  {/* header row 1 */}
                  <tr className="bg-gray-100">
                    <th rowSpan={2} className="text-center whitespace-nowrap">S.No</th>
                    <th rowSpan={2} className="text-center whitespace-nowrap">Branch</th>
                    <th rowSpan={2} className="text-center whitespace-nowrap">Vehicle Name</th>
                    <th rowSpan={2} className="text-center whitespace-nowrap">Vehicle Type</th>
                    {monthDefs.map((m) => (
                      <th key={m.label} colSpan={2} className="text-center whitespace-nowrap">
                        {m.label}
                      </th>
                    ))}
                  </tr>
                  {/* header row 2 */}
                  <tr className="bg-gray-100">
                    {monthDefs.map((m) => (
                      <React.Fragment key={`${m.label}-sub`}>
                        <th className="text-center whitespace-nowrap">Hrs</th>
                        <th className="text-center whitespace-nowrap">KM</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r, idx) => {
                    const branch = pick(r, ["Branch", "BranchName", "fld_branchname"]);
                    const vname = pick(r, ["vehicle_name", "vehiclename", "VehicleName", "fld_vehicle_name"]);
                    const vtype = pick(r, ["vehicletype", "VehicleType", "fld_vehicletypename"]);

                    return (
                      <tr key={idx}>
                        <td className="text-center">{idx + 1}</td>
                        <td className="text-center">{branch || "-"}</td>
                        <td className="text-left">{vname || "-"}</td>
                        <td className="text-center">{vtype || "-"}</td>

                        {monthDefs.map((m) => (
                          <React.Fragment key={`${idx}-${m.label}`}>
                            <td className="text-right">{toNumStr(pick(r, [m.hr], "0.00"), "0.00")}</td>
                            <td className="text-right">{toNumStr(pick(r, [m.km], "0.00"), "0.00")}</td>
                          </React.Fragment>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>

                {/* footer totals */}
                <tfoot>
                  <tr className="bg-gray-100 font-semibold">
                    <td className="text-center" colSpan={4}>TOTAL</td>
                    {monthDefs.map((m) => (
                      <React.Fragment key={`tot-${m.label}`}>
                        <td className="text-right">{toNumStr(totals[m.hr], "0.00")}</td>
                        <td className="text-right">{toNumStr(totals[m.km], "0.00")}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-black p-2 text-[10px] flex justify-between items-center">
            <div>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</div>
            <div className="font-bold">1</div>
          </div>
        </div>
      </div>
    </div>
  );
}